<script lang="ts" generic="TItem" setup>
import { isObject } from 'lodash-es';
import type { Header } from './helpers';
import { getProxy } from './helpers';
import ListViewBase from './ListViewBase.vue';

interface Props
{
    layout?: 'auto' | 'table' | 'flex' | 'card';
    showHeader?: boolean;
    items: TItem[];
    columns?: {
        enabled?: string[];
        visible: Record<string, boolean>;
        positions: Record<string, number>;
    };
    pager?: Pager;
    emptyLabel?: string;
    preload?: boolean;
    draggable?: boolean;
    rowClick?: (item: TItem, i: number, e: PointerEvent) => void;
    rowClass?: (item: TItem, i: number) => Record<string, boolean> | string[] | string;
    customisable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    layout: 'auto',
    showHeader: true,
    columns: () => ({ enabled: [], visible: {}, positions: {} }),
    pager: () => new Pager(1, 20),
    emptyLabel: '[[[Brak wyników]]]',
    preload: true,
    rowClick: (item: TItem, i: number, e: PointerEvent) => {},
    rowClass: (item: TItem, i: number) => ({}),
    draggable: false,
    customisable: true
});

const emit = defineEmits<{
    (e: 'check', value: boolean): void;
    (e: 'drag', value: TItem): void;
    (e: 'change'): void;
}>();

const onChange = (): void =>
{
    emit('change');
};

const onDrag = (value: any): void =>
{
    emit('drag', value);
};

const checkItems = (value: boolean): void =>
{
    emit('check', value);
};

const $slots = useSlots() as any;
const paging = ref(false);
const loaded = ref(!props.preload);

const headers = computed<Header[]>(() =>
{
    if ('row' in $slots)
    {
        const data = getProxy();
        const cols = $slots.row({ item: data }).filter((p: any) => isObject(p.type) && p.type.name.startsWith('ListView'));

        return cols.map((p: any) => ({ ...(p.props || {}), type: p.type.name.replace('ListView', '').toLowerCase() }));
    }

    return [];
});

const rows = computed<TItem[]>(() =>
{
    const preloading = props.layout !== 'card' && props.preload;

    if (preloading && props.pager.loading)
    {
        const data = getProxy();

        return Array.from(Array(5), () => data) as TItem[];
    }

    return props.items || [];
});

watch(() => [props.pager.pageIndex, props.pager.pageSize, props.pager.sorting, props.pager.order], () =>
{
    paging.value = true;
});

// watch(() => props.items, () =>
// {
//     if (paging.value == true)
//     {
//         paging.value = false;
//         loaded.value = true;
//     }

//     if (loaded.value == true && props.items.length == 0)
//     {
//         loaded.value = false;
//     }
//     else if (loaded.value == false)
//     {
//         loaded.value = true;
//     }
// });

// watch(() => loading.value, (value) =>
// {
//     if (value == false && !props.loading)
//     {
//         loaded.value = true;
//     }
// });

watch(() => props.pager.loading, (value) =>
{
    if (typeof value !== 'boolean')
        return;

    loaded.value = !value;
});
</script>

<template>
    <ListViewBase
        :loaded="loaded"
        :layout="props.layout"
        :headers="headers"
        :show-header="props.showHeader"
        :columns="props.columns"
        :items="rows"
        :pager="props.pager"
        :empty-label="props.emptyLabel"
        :row-click="props.rowClick"
        :row-class="props.rowClass"
        :draggable="draggable"
        :customisable="props.customisable"
        @check="checkItems"
        @change="onChange"
        @drag="onDrag"
    >
        <template #row="{ item, index }: { item: TItem, index: number }">
            <slot name="row" :item="item" :index="index" />
        </template>
    </ListViewBase>
</template>
